import { AbstractControl, ValidatorFn } from '@angular/forms';

import { Comparison, createComparisonValidator, createDateComparisonValidator } from './comparison';

// Public functions
/** Creates a validator with a given error key and callback to check for validity. */
export function createGenericValidator(errorKey: string, checkValid: (control?: AbstractControl) => boolean): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        const valid = checkValid(control);
        if (valid) return null;
        else {
            const result = {};
            result[errorKey] = { value: control.value };
            return result;
        }
    };
}

export type TruthyCheckMode = 'truthy' | 'notNullOrUndefined' | 'notNull' | 'notUndefined';

function checkTruthy(value: any, truthyCheckMode: TruthyCheckMode) {
    switch (truthyCheckMode) {
        case 'truthy':
            return value; // Default truthy check
        case 'notNullOrUndefined':
            return value !== null && value !== undefined;
        case 'notNull':
            return value !== null;
        case 'notUndefined':
            return value !== undefined;
    }
}

/** Creates a validators where the given control value is required in case a given condition is true. */
export function createRequiredIfValidator(ifCondition: (control?: AbstractControl) => boolean, truthyCheckMode: TruthyCheckMode = 'truthy'): ValidatorFn {
    const checkValid = (control?: AbstractControl) => {
        return !ifCondition(control) || checkTruthy(control?.value, truthyCheckMode);
    };
    return createGenericValidator('required', checkValid);
}

/** Creates a validators where the given control value must be larger than the value of another given control. */
export function createGreaterThanControlValidator(otherControl: AbstractControl): ValidatorFn {
    return createCompareControlValidator('greaterThan', otherControl, (value, otherValue) => value > otherValue);
}
/** Creates a validators where the given control value must be larger than or equal to the value of another given control. */
export function createGreaterThanOrEqualControlValidator(otherControl: AbstractControl): ValidatorFn {
    return createCompareControlValidator('greaterThanOrEqual', otherControl, (value, otherValue) => value >= otherValue);
}
/** Creates a validators where the given control value must be smaller than the value of another given control. */
export function createSmallerThanControlValidator(otherControl: AbstractControl): ValidatorFn {
    return createCompareControlValidator('smallerThan', otherControl, (value, otherValue) => value < otherValue);
}
/** Creates a validators where the given control value must be smaller than or equal the value of another given control. */
export function createSmallerThanOrEqualControlValidator(otherControl: AbstractControl): ValidatorFn {
    return createCompareControlValidator('smallerThanOrEqual', otherControl, (value, otherValue) => value <= otherValue);
}

// Facade
// NOTE inspired by Validators class from @angular
/** BAR reactive forms validators. */
export class BarValidators {
    /** Creates a validators where the given control value is required in case a given condition is true. */
    static requiredIf(ifCondition: (control?: AbstractControl) => boolean, truthyCheckMode: TruthyCheckMode = 'truthy'): ValidatorFn {
        return createRequiredIfValidator(ifCondition, truthyCheckMode);
    }
    /** Creates a validators where the given control value must be larger than the value of another given control. */
    static greaterThanControl(otherControl: AbstractControl): ValidatorFn {
        return createGreaterThanControlValidator(otherControl);
    }
    /** Creates a validators where the given control value must be larger than or equal to the value of another given control. */
    static greaterThanOrEqualControl(otherControl: AbstractControl): ValidatorFn {
        return createGreaterThanOrEqualControlValidator(otherControl);
    }
    /** Creates a validators where the given control value must be smaller than the value of another given control. */
    static smallerThanControl(otherControl: AbstractControl): ValidatorFn {
        return createSmallerThanControlValidator(otherControl);
    }
    /** Creates a validators where the given control value must be smaller than or equal the value of another given control. */
    static smallerThanOrEqualControl(otherControl: AbstractControl): ValidatorFn {
        return createSmallerThanOrEqualControlValidator(otherControl);
    }
    /** @experimental Creates a validators where the given control date value is compared to to another typed date control using date-fns. */
    static dateComparison(otherControl: AbstractControl<Date>, comparison: Comparison): ValidatorFn {
        return createDateComparisonValidator(otherControl, comparison);
    }
    /** Creates a validators where the given control value is compared to to another control. */
    static comparison(otherControl: AbstractControl<any>, comparison: Comparison): ValidatorFn {
        return createComparisonValidator(otherControl, comparison);
    }
}

// Private functions
function controlValueDefined(control: AbstractControl) {
    return control?.value != null;
}

function createCompareControlValidator(errorKey: string, otherControl: AbstractControl, comparisonCondition: (value: any, otherValue: any) => boolean) {
    return createGenericValidator(errorKey, (control) => {
        // both control values must be defined in able to perform the condition (otherwise it is considered value)
        return !controlValueDefined(control) || !controlValueDefined(otherControl) || comparisonCondition(control.value, otherControl.value);
    });
}

export * from './comparison';
